import { useCallback, useEffect, useMemo, useState, type MouseEvent, type ReactNode } from "react";
import { SCENARIOS, getScenario } from "./data/scenarios";
import { dailyScenarioFor, localDateKey } from "./lib/daily";
import {
  DIMENSION_LABELS,
  STARTING_SCORES,
  applyChoice,
  scoreLabel,
} from "./lib/scoring";
import { clearProgress, freshSession, loadSession, saveSession } from "./lib/storage";
import { DIMENSIONS, type Choice, type Dimension, type Scenario, type SessionProgress } from "./lib/types";

function usePathname(): [string, (path: string) => void] {
  const [pathname, setPathname] = useState(() => window.location.pathname);

  useEffect(() => {
    const onPopState = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const navigate = useCallback((path: string) => {
    if (window.location.pathname !== path) window.history.pushState({}, "", path);
    setPathname(path);
    window.scrollTo({
      top: 0,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
    });
  }, []);

  return [pathname, navigate];
}

function Link({ to, navigate, className, children }: { to: string; navigate: (path: string) => void; className?: string; children: ReactNode }) {
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      event.preventDefault();
      navigate(to);
    }
  };

  return <a href={to} onClick={onClick} className={className}>{children}</a>;
}

function Spark({ className = "" }: { className?: string }) {
  return <span className={`spark ${className}`} aria-hidden="true">✦</span>;
}

function Header({ pathname, navigate }: { pathname: string; navigate: (path: string) => void }) {
  return (
    <header className="site-header">
      <Link to="/" navigate={navigate} className="wordmark" aria-label="Oratrice home">
        <span className="wordmark-kicker">small talk</span>
        <span className="wordmark-main"><Spark /> Oratrice <Spark /></span>
        <span className="wordmark-sub">arcade</span>
      </Link>
      <nav className="main-nav" aria-label="Main navigation">
        <Link to="/" navigate={navigate} className={pathname === "/" ? "active" : undefined}>Scene menu</Link>
        <Link to="/daily" navigate={navigate} className={pathname === "/daily" ? "active daily-nav" : "daily-nav"}>Today's special</Link>
      </nav>
    </header>
  );
}

function Footer({ onClear, notice }: { onClear: () => void; notice: string }) {
  return (
    <footer className="site-footer">
      <div>
        <span className="footer-mark">ORATRICE</span>
        <p>A private, no-pressure practice counter. No account, microphone, AI, or remote data collection.</p>
      </div>
      <div className="footer-links">
        <a href="/#about">About the approach</a>
        <button type="button" className="text-button" onClick={onClear}>Clear saved progress</button>
        <span className="clear-notice" role="status">{notice}</span>
      </div>
    </footer>
  );
}

function ScenarioCard({ scenario, navigate }: { scenario: Scenario; navigate: (path: string) => void }) {
  const progress = loadSession(scenario.id);
  return (
    <article className={`scenario-card accent-${scenario.accent}`}>
      <div className="card-number" aria-hidden="true">{scenario.number}</div>
      <div className="card-topline">
        <span>{scenario.location}</span>
        <span>{scenario.time}</span>
      </div>
      <h3>{scenario.shortTitle}</h3>
      <p className="scenario-title">“{scenario.title}”</p>
      <p>{scenario.description}</p>
      <div className="card-bottom">
        <span>{scenario.rounds.length} rounds</span>
        <Link to={`/play/${scenario.id}`} navigate={navigate} className="card-play">
          {progress && !progress.completed ? "Continue" : progress?.completed ? "Play again" : "Take a seat"}
          <span aria-hidden="true">→</span>
        </Link>
      </div>
    </article>
  );
}

function DimensionExplainer({ dimension, index }: { dimension: Dimension; index: number }) {
  const copy: Record<Dimension, string> = {
    warmth: "Do your words acknowledge the person—not just the topic?",
    curiosity: "Do you make room for another beat without interrogating?",
    boundaries: "Do you notice privacy, power, time, and your own limits?",
    exitTiming: "Can you hear when a conversation is opening or closing?",
  };
  return (
    <article className="dimension-note">
      <span>{String(index + 1).padStart(2, "0")}</span>
      <h3>{DIMENSION_LABELS[dimension]}</h3>
      <p>{copy[dimension]}</p>
    </article>
  );
}

function Home({ navigate }: { navigate: (path: string) => void }) {
  const todayKey = localDateKey();
  const daily = dailyScenarioFor(todayKey, SCENARIOS);
  const readableDate = new Intl.DateTimeFormat("en-US", { weekday: "long", month: "long", day: "numeric" }).format(new Date());

  return (
    <main>
      <section className="hero">
        <div className="hero-copy">
          <div className="open-sign"><span>OPEN</span> NO PRESSURE</div>
          <p className="eyebrow">A conversation practice counter</p>
          <h1>Small talk isn't small when you're <em>new to the room.</em></h1>
          <p className="hero-deck">
            Practice the tiny choices that keep everyday American conversation moving—how to join in, stay curious, hold a boundary, and leave well.
          </p>
          <div className="hero-actions">
            <Link to="/daily" navigate={navigate} className="button button-primary">Play today's special <span>→</span></Link>
            <a href="#scene-menu" className="button button-quiet">Browse all scenes</a>
          </div>
        </div>
        <div className="daily-ticket" aria-label={`Today's special: ${daily.shortTitle}`}>
          <div className="ticket-pins" aria-hidden="true"><i /><i /><i /><i /><i /></div>
          <div className="ticket-date">Today's special · {readableDate}</div>
          <div className="ticket-art" aria-hidden="true">
            <span className="coffee-ring" />
            <span className="ticket-star">✶</span>
          </div>
          <p className="ticket-number">ORDER № {daily.number}</p>
          <h2>{daily.shortTitle}</h2>
          <p>“{daily.title}”</p>
          <div className="ticket-rule" />
          <span>{daily.invitation}</span>
          <Link to="/daily" navigate={navigate}>START ORDER <span aria-hidden="true">↗</span></Link>
        </div>
      </section>

      <div className="counter-strip" aria-hidden="true">
        <span>6 everyday scenes</span><Spark /><span>3 ways to answer</span><Spark /><span>zero perfect scripts</span><Spark /><span>open 24 hours</span>
      </div>

      <section className="scene-section" id="scene-menu">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Choose your booth</p>
            <h2>The scene menu</h2>
          </div>
          <p>Each scene takes about four minutes. Your choices change the reply and your conversational mix—not a pass/fail grade.</p>
        </div>
        <div className="scenario-grid">
          {SCENARIOS.map((scenario) => <ScenarioCard key={scenario.id} scenario={scenario} navigate={navigate} />)}
        </div>
      </section>

      <section className="score-section">
        <div className="score-sign">
          <span className="score-sign-small">THE HOUSE LOOKS FOR</span>
          <strong>4 SIGNALS</strong>
          <span>NOT “PERFECT ENGLISH”</span>
        </div>
        <div className="dimension-grid">
          {DIMENSIONS.map((dimension, index) => <DimensionExplainer key={dimension} dimension={dimension} index={index} />)}
        </div>
      </section>

      <section className="about-section" id="about">
        <div className="about-label">HOUSE NOTE № 1</div>
        <div className="about-copy">
          <p className="eyebrow">About this arcade</p>
          <h2>Context is the real high score.</h2>
          <p>
            Small talk changes by region, community, age, relationship, personality, and access needs. Oratrice offers possible readings—not a single correct way to “sound American.” A direct answer may be exactly right. A warm answer may still be intrusive. Your judgment matters.
          </p>
          <p>
            The scenarios are original. For learners who want more language material, the U.S. Department of State's American English program publishes free
            {" "}<a href="https://americanenglish.state.gov/resources/introductions-and-small-talk" target="_blank" rel="noreferrer">introductions and small-talk resources</a>.
          </p>
        </div>
        <div className="house-rules">
          <h3>Counter rules</h3>
          <ul>
            <li><span>01</span>No login or profile</li>
            <li><span>02</span>No microphone or recording</li>
            <li><span>03</span>Progress stays in this browser</li>
            <li><span>04</span>Leave any scene whenever you like</li>
          </ul>
        </div>
      </section>
    </main>
  );
}

function Meter({ dimension, score, previous }: { dimension: Dimension; score: number; previous?: number }) {
  const delta = previous === undefined ? 0 : score - previous;
  return (
    <div className="meter">
      <div className="meter-label">
        <span>{DIMENSION_LABELS[dimension]}</span>
        <span>{score}<small>/10</small>{delta !== 0 && <b className={delta > 0 ? "up" : "down"}>{delta > 0 ? ` +${delta}` : ` ${delta}`}</b>}</span>
      </div>
      <div className="meter-track" aria-label={`${DIMENSION_LABELS[dimension]} ${score} out of 10`} role="meter" aria-valuemin={0} aria-valuemax={10} aria-valuenow={score}>
        <span style={{ width: `${score * 10}%` }} />
      </div>
    </div>
  );
}

function normalizeSession(scenario: Scenario): SessionProgress {
  const saved = loadSession(scenario.id);
  if (!saved) return freshSession(scenario.id, scenario.rounds[0].id);
  if (saved.completed) return saved;
  if (!saved.roundId || !scenario.rounds.some((round) => round.id === saved.roundId)) {
    return freshSession(scenario.id, scenario.rounds[0].id);
  }
  return saved;
}

function ResultScreen({ scenario, session, navigate, onReplay }: { scenario: Scenario; session: SessionProgress; navigate: (path: string) => void; onReplay: () => void }) {
  const strongest = [...DIMENSIONS].sort((a, b) => session.scores[b] - session.scores[a])[0];
  return (
    <main className="game-shell result-shell">
      <div className="result-marquee" aria-hidden="true"><Spark /><span>CHECK, PLEASE!</span><Spark /></div>
      <section className="result-card">
        <p className="eyebrow">Scene complete · {scenario.shortTitle}</p>
        <h1>Your conversation had <em>{DIMENSION_LABELS[strongest].toLowerCase()}</em> on the jukebox.</h1>
        <p className="result-intro">This is a snapshot of the route you took, not a verdict. Replay and choose differently to see how another tone changes the room.</p>
        <div className="result-meters">
          {DIMENSIONS.map((dimension) => (
            <div className="result-score" key={dimension}>
              <span>{DIMENSION_LABELS[dimension]}</span>
              <strong>{session.scores[dimension]}</strong>
              <small>{scoreLabel(session.scores[dimension])}</small>
            </div>
          ))}
        </div>
        <div className="result-note">
          <strong>One last counter note</strong>
          <p>A lower number can reflect a direct boundary, a hurried moment, or simply one interpretation of tone. Real people and settings vary; use the feedback as a question, not a rulebook.</p>
        </div>
        <div className="result-actions">
          <button type="button" className="button button-primary" onClick={onReplay}>Replay this scene <span>↻</span></button>
          <Link to="/" navigate={navigate} className="button button-quiet">Back to the menu</Link>
        </div>
      </section>
    </main>
  );
}

function Game({ scenario, navigate, daily = false }: { scenario: Scenario; navigate: (path: string) => void; daily?: boolean }) {
  const [session, setSession] = useState<SessionProgress>(() => normalizeSession(scenario));
  const [selectedChoice, setSelectedChoice] = useState<Choice | null>(null);
  const [previewScores, setPreviewScores] = useState(session.scores);

  const round = scenario.rounds.find((item) => item.id === session.roundId);
  const roundIndex = round ? scenario.rounds.indexOf(round) : scenario.rounds.length;

  const choose = useCallback((choice: Choice) => {
    if (selectedChoice) return;
    setSelectedChoice(choice);
    setPreviewScores(applyChoice(session.scores, choice));
  }, [selectedChoice, session.scores]);

  const continueConversation = useCallback(() => {
    if (!selectedChoice) return;
    const next: SessionProgress = {
      scenarioId: scenario.id,
      roundId: selectedChoice.nextRoundId,
      scores: previewScores,
      completed: selectedChoice.nextRoundId === null,
    };
    saveSession(next);
    setSession(next);
    setSelectedChoice(null);
    window.scrollTo({
      top: 0,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
    });
  }, [previewScores, scenario.id, selectedChoice]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.matches("input, textarea, select")) return;
      if (!selectedChoice && round && ["1", "2", "3"].includes(event.key)) {
        event.preventDefault();
        choose(round.choices[Number(event.key) - 1]);
      } else if (selectedChoice && event.key === "Enter") {
        event.preventDefault();
        continueConversation();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [choose, continueConversation, round, selectedChoice]);

  const replay = () => {
    const next = freshSession(scenario.id, scenario.rounds[0].id);
    saveSession(next);
    setSession(next);
    setPreviewScores(next.scores);
    setSelectedChoice(null);
  };

  if (session.completed || !round) {
    return <ResultScreen scenario={scenario} session={session} navigate={navigate} onReplay={replay} />;
  }

  return (
    <main className="game-shell">
      <section className="game-topbar">
        <Link to="/" navigate={navigate} className="back-link"><span aria-hidden="true">←</span> Leave scene</Link>
        <div className="scene-ident">
          {daily && <span className="daily-stamp">Today's special</span>}
          <span>Scene {scenario.number}</span>
          <strong>{scenario.shortTitle}</strong>
        </div>
        <div className="round-count"><span>ROUND</span><strong>{roundIndex + 1}</strong><small>/{scenario.rounds.length}</small></div>
      </section>

      <div className="game-layout">
        <aside className="score-console" aria-label="Conversation signals">
          <div className="console-lights" aria-hidden="true"><i /><i /><i /></div>
          <h2>Signal board</h2>
          <p>One possible reading of this route</p>
          <div className="meters">
            {DIMENSIONS.map((dimension) => (
              <Meter
                key={dimension}
                dimension={dimension}
                score={selectedChoice ? previewScores[dimension] : session.scores[dimension]}
                previous={selectedChoice ? session.scores[dimension] : undefined}
              />
            ))}
          </div>
          <div className="console-tip"><Spark /> Keyboard: press 1, 2, or 3</div>
        </aside>

        <section className="conversation-stage">
          <div className="scene-setting">
            <div><span>{scenario.location}</span><strong>{scenario.time}</strong></div>
            <p>{round.stageNote}</p>
          </div>

          <div className="speaker-line">
            <div className="speaker-avatar" aria-hidden="true"><span>{scenario.cast.charAt(0)}</span></div>
            <div className="speech-wrap">
              <span className="speaker-name">{scenario.cast}</span>
              <blockquote>“{round.npcLine}”</blockquote>
            </div>
          </div>

          {!selectedChoice ? (
            <div className="choice-area">
              <div className="choice-heading"><span>YOUR TURN</span><p>Choose the reply that fits the moment you want.</p></div>
              <div className="choices">
                {round.choices.map((choice, index) => (
                  <button type="button" className="choice-button" key={choice.id} onClick={() => choose(choice)}>
                    <kbd>{index + 1}</kbd>
                    <span className="choice-copy"><small>{choice.signal}</small><strong>{choice.label}</strong></span>
                    <span className="choice-arrow" aria-hidden="true">→</span>
                  </button>
                ))}
              </div>
              <p className="choice-reminder">There is no universal best line. Try the one you might actually say.</p>
            </div>
          ) : (
            <div className="feedback-panel" aria-live="polite">
              <div className="your-line"><span>You chose · {selectedChoice.signal}</span><p>“{selectedChoice.label}”</p></div>
              <div className="reply-line"><span>{scenario.cast.split(",")[0].split("+")[0].trim()} replies</span><blockquote>“{selectedChoice.reply}”</blockquote></div>
              <div className="coach-note">
                <div className="receipt-label">COUNTER READ</div>
                <p>{selectedChoice.coach}</p>
                <span>Other people, regions, and relationships may read the same words differently.</span>
              </div>
              <button type="button" className="button button-primary continue-button" onClick={continueConversation} autoFocus>
                {selectedChoice.nextRoundId ? "Continue the conversation" : "See your conversation mix"}<span>→</span>
                <kbd>ENTER</kbd>
              </button>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

function NotFound({ navigate }: { navigate: (path: string) => void }) {
  return (
    <main className="not-found">
      <div className="closed-sign">WRONG TURN</div>
      <p className="eyebrow">This booth is empty</p>
      <h1>Nothing is being served at this address.</h1>
      <Link to="/" navigate={navigate} className="button button-primary">Return to the scene menu</Link>
    </main>
  );
}

export default function App() {
  const [pathname, navigate] = usePathname();
  const [storageEpoch, setStorageEpoch] = useState(0);
  const [notice, setNotice] = useState("");
  const daily = useMemo(() => dailyScenarioFor(localDateKey(), SCENARIOS), []);

  const clearSavedProgress = () => {
    clearProgress();
    setStorageEpoch((value) => value + 1);
    setNotice("Saved progress cleared.");
    window.setTimeout(() => setNotice(""), 3000);
  };

  let content: ReactNode;
  if (pathname === "/") {
    content = <Home key={storageEpoch} navigate={navigate} />;
  } else if (pathname === "/daily") {
    content = <Game key={`daily-${daily.id}-${storageEpoch}`} scenario={daily} navigate={navigate} daily />;
  } else if (pathname.startsWith("/play/")) {
    const scenario = getScenario(decodeURIComponent(pathname.slice("/play/".length)).replace(/\/$/, ""));
    content = scenario ? <Game key={`${scenario.id}-${storageEpoch}`} scenario={scenario} navigate={navigate} /> : <NotFound navigate={navigate} />;
  } else {
    content = <NotFound navigate={navigate} />;
  }

  return (
    <div className="app-frame">
      <a href="#main-content" className="skip-link">Skip to content</a>
      <Header pathname={pathname} navigate={navigate} />
      <div id="main-content" tabIndex={-1}>{content}</div>
      <Footer onClear={clearSavedProgress} notice={notice} />
    </div>
  );
}
